<script setup lang="ts">
  import { onBeforeUnmount, onMounted, ref } from 'vue'
  import { useState } from '~renderer/composables/useState'

  const AUTO_DISMISS_MS = 6000
  const { errors, removeError } = useState()
  const timers = ref<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const scheduleAutoDismiss = (id: string) => {
    if (timers.value.has(id)) return
    const handle = setTimeout(() => dismiss(id), AUTO_DISMISS_MS)
    timers.value.set(id, handle)
  }

  const dismiss = (id: string) => {
    const handle = timers.value.get(id)
    if (handle) clearTimeout(handle)
    timers.value.delete(id)
    removeError(id)
  }

  onMounted(() => errors.value.forEach((e) => scheduleAutoDismiss(e.id)))

  onBeforeUnmount(() => {
    timers.value.forEach((handle) => clearTimeout(handle))
    timers.value.clear()
  })

  const onEnter = (id: string) => scheduleAutoDismiss(id)
</script>

<template>
  <Teleport to="#teleported">
    <div
      id="error-toasts-container"
      class="pointer-events-none fixed inset-x-0 top-0 z-9999 flex flex-col items-center gap-3 p-4 sm:items-end sm:p-6"
    >
      <TransitionGroup name="toast">
        <div
          v-for="error in errors"
          :key="error.id"
          class="pointer-events-auto relative w-full max-w-sm overflow-hidden rounded-xl border shadow-2xl backdrop-blur-xl"
          :class="[
            error.severity === 'error'
              ? 'border-red-400/30 bg-[#271920]/90'
              : 'border-yellow-400/30 bg-[#272118]/90',
          ]"
          @vue:mounted="onEnter(error.id)"
        >
          <!-- Content -->
          <div class="flex items-start gap-3 p-4">
            <!-- Severity Icon -->
            <div
              class="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg"
              :class="[
                error.severity === 'error'
                  ? 'bg-red-500/15 text-red-400'
                  : 'bg-yellow-500/15 text-yellow-400',
              ]"
            >
              <i-fa7-solid-circle-exclamation v-if="error.severity === 'error'" class="size-4" />
              <i-fa7-solid-triangle-exclamation v-else class="size-4" />
            </div>

            <!-- Message -->
            <div class="min-w-0 flex-1">
              <p
                class="text-xs font-bold tracking-wider uppercase"
                :class="[error.severity === 'error' ? 'text-red-400' : 'text-yellow-400']"
              >
                {{ error.severity === 'error' ? 'Error' : 'Warning' }}
              </p>
              <p class="text-tan-200 mt-1 text-sm leading-snug">
                {{ error.message }}
              </p>
              <p class="text-tan-500 mt-1 font-mono text-[10px]">
                {{ error.code }}
              </p>
            </div>

            <!-- Dismiss Button -->
            <button
              @click="dismiss(error.id)"
              class="text-tan-500 shrink-0 cursor-pointer rounded-md p-1 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Dismiss"
            >
              <i-fa7-solid-xmark class="size-3.5" />
            </button>
          </div>

          <!-- Auto-dismiss progress bar -->
          <div class="h-[2px] w-full overflow-hidden bg-black/20">
            <div
              class="h-full origin-left"
              :class="[error.severity === 'error' ? 'bg-red-400/60' : 'bg-yellow-400/60']"
              :style="{ animation: `toast-progress ${AUTO_DISMISS_MS}ms linear forwards` }"
            />
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
  /* --- Enter / Leave transitions --- */
  .toast-enter-active {
    transition: all 0.35s cubic-bezier(0.21, 1.02, 0.73, 1);
  }

  .toast-leave-active {
    transition: all 0.25s cubic-bezier(0.4, 0, 1, 1);
  }

  .toast-enter-from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }

  @media (min-width: 640px) {
    .toast-enter-from {
      transform: translateX(40px) scale(0.95);
    }
  }

  .toast-leave-to {
    opacity: 0;
    transform: translateX(40px) scale(0.95);
  }

  .toast-move {
    transition: transform 0.3s ease;
  }

  /* --- Progress bar shrink --- */
  @keyframes toast-progress {
    from {
      transform: scaleX(1);
    }
    to {
      transform: scaleX(0);
    }
  }
</style>
